import React, { useEffect, useMemo, useState } from 'react';
import { SettingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import BaseSelect from 'components/molecules/select/base';
import BaseModal from 'components/atoms/base-modal';
import BaseButton from 'components/atoms/baseButton';
import BaseTag from 'components/atoms/base-tag';

import s from './styles.module.scss';
import ClinicFilterForm from './form';

import type { ISelectedClinics } from './form';

interface IProps {
  agencyGuid?: string[];
  included?: string[];
  parentClinicGuid?: string[];
  onChange: (props: ISelectedClinics) => void;
  showOnInteractiveTimeDashboard?: boolean;
  useAdminApi?: boolean;
  withoutLabel?: boolean;
}

const ClinicFilterSelectBoxV2: React.FC<IProps> = ({
  agencyGuid,
  included = [],
  parentClinicGuid = [],
  showOnInteractiveTimeDashboard,
  useAdminApi,
  onChange,
  withoutLabel = false,
}) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [selectedClinics, setSelectedClinics] = useState<ISelectedClinics>({
    included,
    parentClinicGuid,
  });
  const [selectedStats, setSelectedStats] = useState<{
    total: number;
    parents: number;
    children: number;
  } | null>(null);

  const includedOption = useMemo(
    () => [
      {
        label: `${t('labels.included')} (${
          included.length + parentClinicGuid.length
        })`,
        value: 'included',
      },
    ],
    [included.length, parentClinicGuid.length],
  );

  const onApply = () => {
    onChange({
      included: selectedClinics.included ?? [],
      parentClinicGuid: selectedClinics.parentClinicGuid ?? [],
    });
    setOpen(false);
  };

  const onClear = () => {
    setSelectedClinics({ included: [], parentClinicGuid: [] });
    onChange({
      included: [],
      parentClinicGuid: [],
    });
  };

  useEffect(() => {
    setSelectedClinics({ included, parentClinicGuid });
  }, [included.length, parentClinicGuid.length]);

  const sharedSelectProps = {
    mode: 'multiple' as const,
    allowClear: true,
    dropdownRender: () => <></>,
    suffixIcon: <SettingOutlined />,
    open: false,
    tagRender: ({ label }: { label: React.ReactNode }) => (
      <BaseTag label={`${label}`} />
    ),
    showSearch: false,
  };

  return (
    <>
      <div className={s['select-wrapper']}>
        <BaseSelect
          label={withoutLabel ? undefined : t('labels.clinics')}
          placeholder={t('labels.all')}
          data-cy="filter-clinicsList-included"
          options={includedOption}
          value={
            included.length || parentClinicGuid.length
              ? [
                  {
                    label: `${t('labels.selected')} (${
                      included.length + parentClinicGuid.length
                    })`,
                    value: 'included',
                  },
                ]
              : []
          }
          onClick={() => setOpen(true)}
          onClear={onClear}
          {...sharedSelectProps}
        />
      </div>
      <BaseModal
        title={t('labels.clinics')}
        open={open}
        width="70%"
        bodyClassNames={s['modal-body']}
        withFooter={
          <div className={s['modal-footer']}>
            <span className={s['footer-stats']}>
              {selectedStats && (
                <>
                  {t('labels.selected')}: {selectedStats.total}{' '}
                  {selectedStats.total === 1
                    ? t('labels.clinic')
                    : t('labels.clinics')}
                </>
              )}
            </span>
            <div className={s['footer-actions']}>
              <BaseButton
                key="clear"
                label={t('controls.clear')}
                type="secondary"
                onClick={() => {
                  onClear();
                }}
                className={s.button}
                dataCy="clinicList-clear-button"
              />
              <BaseButton
                key="cancel"
                label={t('controls.cancel')}
                type="secondary"
                onClick={() => setOpen(false)}
                className={s.button}
                dataCy="clinicList-cancel-button"
              />
              <BaseButton
                key="apply"
                label={t('controls.apply')}
                onClick={onApply}
                className={s.button}
                dataCy="clinicList-apply-button"
              />
            </div>
          </div>
        }
        onCancel={() => setOpen(false)}
      >
        {open && (
          <ClinicFilterForm
            agencyGuid={agencyGuid}
            included={selectedClinics.included}
            parentClinicGuid={selectedClinics.parentClinicGuid}
            onChange={setSelectedClinics}
            onStatsChange={setSelectedStats}
            useAdminApi={useAdminApi}
            showOnInteractiveTimeDashboard={showOnInteractiveTimeDashboard}
          />
        )}
      </BaseModal>
    </>
  );
};

export default ClinicFilterSelectBoxV2;
